import { Col, Row, Text } from 'components';
import { theme } from 'lib';
import React, { useMemo } from 'react';
import { isTerminalStatus } from '../constants';
import { CONFLICT_KIND, segmentFill } from '../timeline';
import { MATCH_ACCENT } from './ui';
import { fmtDay, fmtSpan, fmtTime, Notice } from './timelineUi';

// CALENDAR ZOOM — the same waves over a date range instead of one fixture.
//
// This is where a long wave is visibly a problem. A wave anchored half-time → a distant date-time
// sits across the NEXT fixture, and because only one wave holds a team's session at a time, the
// next fixture's wave never opens. On a single-match track that is invisible; here it is a bar
// lying over another fixture's marker.
//
// One day per lane, each lane a 24-hour axis. A wave spanning midnight is clipped into a bar in
// every day it touches, which is what makes its length legible — a single bar on a week-wide axis
// would be a sliver.

const DAY_MS = 24 * 60 * 60 * 1000;
const LANE_HEIGHT = 30;

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const SEGMENT_PAINT = {
  filled: { bg: MATCH_ACCENT.rail, col: '#fff', dash: false },
  dashed: { bg: MATCH_ACCENT.chip, col: '#1E3A8A', dash: true },
  muted: { bg: theme.GREY_20, col: theme.GREY_70, dash: false }
};

/** Every day the range covers, so an empty day still gets a lane and reads as empty. */
const daysBetween = (from, to) => {
  const days = [];
  let cursor = startOfDay(from);
  const last = startOfDay(to);
  // Guard the loop rather than trusting the caller's range: a mis-ordered pair would spin.
  let safety = 0;
  while (cursor <= last && safety < 400) {
    days.push(cursor);
    cursor = new Date(cursor.getTime() + DAY_MS);
    safety += 1;
  }
  return days;
};

const HOUR_TICKS = [0, 6, 12, 18];

const CalendarZoom = ({ timeline, from, to, games, onOpenMatch }) => {
  const days = useMemo(() => daysBetween(from, to), [from, to]);

  // Fixture markers. Calendar zoom returns `phases: []` — the endpoint reports phase boundaries
  // for a single match only — so the kick-off pins come from the channel's fixture list, which is
  // already loaded for the match picker. The WAVES, which are what the view is about, are the
  // timeline response's.
  const fixtures = useMemo(
    () =>
      (games || [])
        .map((game) => ({ id: game._id, at: game.date ? new Date(game.date) : null, game }))
        .filter((f) => f.at && !Number.isNaN(f.at.getTime()) && f.at >= from && f.at <= to),
    [games, from, to]
  );

  const blockedIds = useMemo(
    () =>
      new Set(
        (timeline?.conflicts || [])
          .filter((c) => c.kind === CONFLICT_KIND.BLOCKED)
          .map((c) => c.waveId)
      ),
    [timeline]
  );

  const blockers = useMemo(
    () =>
      new Set(
        (timeline?.conflicts || [])
          .filter((c) => c.kind === CONFLICT_KIND.BLOCKED)
          .map((c) => c.otherWaveId)
      ),
    [timeline]
  );

  if (!timeline) return null;

  const now = new Date();

  return (
    <Col noFlex wid="100%" gap="10px">
      {blockedIds.size > 0 && (
        <Notice tone="danger" title={`${blockedIds.size} wave${blockedIds.size > 1 ? 's' : ''} will never open`}>
          A wave whose window sits inside a wave on another fixture never gets the session. The
          blocking wave is outlined below — shorten its close and the blocked one can run.
        </Notice>
      )}

      {/* Hour scale, once, above the lanes. */}
      <Row noFlex wid="100%" gap="8px" style={{ alignItems: 'flex-end' }}>
        <div style={{ width: 92, flexShrink: 0 }} />
        <div style={{ position: 'relative', flex: 1, height: 14 }}>
          {HOUR_TICKS.map((hour) => (
            <Text
              key={hour}
              c1
              col={theme.GREY_50}
              marg="0"
              style={{
                position: 'absolute',
                left: `${(hour / 24) * 100}%`,
                fontSize: 9.5,
                transform: hour === 0 ? 'none' : 'translateX(-50%)'
              }}
            >
              {`${String(hour).padStart(2, '0')}:00`}
            </Text>
          ))}
        </div>
      </Row>

      {days.map((day) => {
        const dayStart = day.getTime();
        const dayEnd = dayStart + DAY_MS;
        const isToday = startOfDay(now).getTime() === dayStart;

        const bars = timeline.waves
          .filter((wave) => wave.opensAt.getTime() < dayEnd && wave.closesAt.getTime() > dayStart)
          .map((wave) => {
            const start = Math.max(wave.opensAt.getTime(), dayStart);
            const end = Math.min(wave.closesAt.getTime(), dayEnd);
            return {
              wave,
              left: ((start - dayStart) / DAY_MS) * 100,
              width: Math.max(0.5, ((end - start) / DAY_MS) * 100),
              // A bar clipped at either end says so, so a two-day wave does not read as two waves.
              clippedStart: wave.opensAt.getTime() < dayStart,
              clippedEnd: wave.closesAt.getTime() > dayEnd
            };
          });

        const dayFixtures = fixtures.filter(
          (f) => f.at.getTime() >= dayStart && f.at.getTime() < dayEnd
        );

        return (
          <Row key={dayStart} noFlex wid="100%" gap="8px" style={{ alignItems: 'stretch' }}>
            <Col noFlex style={{ width: 92, flexShrink: 0, justifyContent: 'center' }}>
              <Text
                c1
                bold={isToday}
                col={isToday ? theme.SECONDARY : theme.GREY_70}
                marg="0"
                style={{ fontSize: 11 }}
              >
                {fmtDay(day)}
              </Text>
            </Col>

            <div
              style={{
                position: 'relative',
                flex: 1,
                height: LANE_HEIGHT,
                borderRadius: 6,
                border: `1px solid ${isToday ? MATCH_ACCENT.border : theme.GREY_20}`,
                background: isToday ? MATCH_ACCENT.panel : '#fff',
                overflow: 'hidden'
              }}
            >
              {HOUR_TICKS.filter((h) => h > 0).map((hour) => (
                <div
                  key={hour}
                  style={{
                    position: 'absolute',
                    left: `${(hour / 24) * 100}%`,
                    top: 0,
                    bottom: 0,
                    width: 1,
                    background: theme.GREY_10
                  }}
                />
              ))}

              {dayFixtures.map((fixture) => (
                <div
                  key={fixture.id}
                  title={`Kick-off ${fmtTime(fixture.at)}`}
                  style={{
                    position: 'absolute',
                    left: `${((fixture.at.getTime() - dayStart) / DAY_MS) * 100}%`,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: theme.GREY_60,
                    zIndex: 2,
                    pointerEvents: 'none'
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 1,
                      left: -3,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: theme.GREY_60
                    }}
                  />
                </div>
              ))}

              {bars.map(({ wave, left, width, clippedStart, clippedEnd }) => {
                const paint = SEGMENT_PAINT[segmentFill(wave)] || SEGMENT_PAINT.dashed;
                const isBlocked = blockedIds.has(wave.waveId);
                const isBlocker = blockers.has(wave.waveId);
                const minutes = Math.round(
                  (wave.closesAt.getTime() - wave.opensAt.getTime()) / 60000
                );
                return (
                  <div
                    key={`${wave.waveId}-${dayStart}`}
                    onClick={() => wave.gameId && onOpenMatch && onOpenMatch(wave.gameId)}
                    title={`${fmtTime(wave.opensAt)} → ${fmtTime(wave.closesAt)} · ${fmtSpan(
                      minutes
                    )} · ${wave.status}${wave.pending ? ' · projected' : ''}${
                      isBlocked ? ' · will never open' : ''
                    }${isBlocker ? ' · holds the session across another fixture' : ''}`}
                    style={{
                      position: 'absolute',
                      left: `${left}%`,
                      width: `${width}%`,
                      top: 4,
                      bottom: 4,
                      borderRadius: 4,
                      background: isBlocked ? '#FEE2E2' : paint.bg,
                      border: isBlocked
                        ? '1.5px solid #DC2626'
                        : isBlocker
                          ? '1.5px solid #D97706'
                          : `1px ${paint.dash || wave.pending ? 'dashed' : 'solid'} ${
                              paint.dash ? MATCH_ACCENT.rail : 'transparent'
                            }`,
                      // A clipped edge loses its rounding, so the bar reads as continuing.
                      borderTopLeftRadius: clippedStart ? 0 : 4,
                      borderBottomLeftRadius: clippedStart ? 0 : 4,
                      borderTopRightRadius: clippedEnd ? 0 : 4,
                      borderBottomRightRadius: clippedEnd ? 0 : 4,
                      cursor: wave.gameId ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      opacity: isTerminalStatus(wave.status) ? 0.7 : 1,
                      zIndex: 1
                    }}
                  >
                    {width > 12 && (
                      <Text
                        c1
                        bold
                        col={isBlocked ? '#991B1B' : paint.col}
                        marg="0"
                        style={{
                          fontSize: 9.5,
                          whiteSpace: 'nowrap',
                          padding: '0 5px',
                          pointerEvents: 'none'
                        }}
                      >
                        {wave.mode === 'non_game_day' ? 'Scheduled' : fmtTime(wave.opensAt)}
                      </Text>
                    )}
                  </div>
                );
              })}

              {isToday && now.getTime() >= dayStart && now.getTime() < dayEnd && (
                <div
                  title={`Now · ${fmtTime(now)}`}
                  style={{
                    position: 'absolute',
                    left: `${((now.getTime() - dayStart) / DAY_MS) * 100}%`,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: theme.SECONDARY,
                    zIndex: 3,
                    pointerEvents: 'none'
                  }}
                />
              )}
            </div>
          </Row>
        );
      })}

      {!timeline.waves.length && (
        <Col
          centerAll
          noFlex
          wid="100%"
          pad="24px 0"
          style={{ borderRadius: 10, border: `1px dashed ${theme.GREY_30}`, background: theme.GREY_10 }}
        >
          <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 12 }}>
            No waves in this range.
          </Text>
        </Col>
      )}

      <Row center noFlex gap="14px" style={{ flexWrap: 'wrap' }}>
        <Legend swatch={{ background: MATCH_ACCENT.rail }} label="Running" />
        <Legend
          swatch={{ background: MATCH_ACCENT.chip, border: `1px dashed ${MATCH_ACCENT.rail}` }}
          label="Scheduled / projected"
        />
        <Legend swatch={{ background: theme.GREY_20 }} label="Finished" />
        <Legend swatch={{ background: '#FEE2E2', border: '1.5px solid #DC2626' }} label="Never opens" />
        <Legend swatch={{ background: '#fff', border: '1.5px solid #D97706' }} label="Holds the session" />
        <Legend swatch={{ background: theme.GREY_60, width: 2 }} label="Kick-off" />
      </Row>
    </Col>
  );
};

const Legend = ({ swatch, label }) => (
  <Row center noFlex gap="5px">
    <span
      style={{
        width: swatch.width || 14,
        height: 10,
        borderRadius: 3,
        display: 'inline-block',
        ...swatch
      }}
    />
    <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 10.5 }}>
      {label}
    </Text>
  </Row>
);

export const CALENDAR_RANGES = [
  { key: 'week', label: 'Week', days: 7 },
  { key: 'month', label: 'Month', days: 30 }
];

export default CalendarZoom;
